import React, { useContext, useState } from "react";

import {
  TextField,
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";

import {
  LockOutlined,
  EmailOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

import { api } from "@api/api";
import { CondicaoContext } from "../../contexto";
import IdentidadeVisual from "../../estilos/identidadeVisual";
import BotaoGuiaUtilizacao from "../../componentes/comuns/BotaoGuiaUtilizacao";

const AZUL_LOGIN = "#0060ef";

const campoLoginSx = {
  "& .MuiInputLabel-root": {
    color: AZUL_LOGIN,
    fontSize: "14px",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: AZUL_LOGIN,
  },

  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "6px",
    backgroundColor: "rgba(232, 242, 255, 0.45)",
    color: "#182039",
    fontSize: "13px",

    "& fieldset": {
      borderColor: "#78a3ff",
    },

    "&:hover fieldset": {
      borderColor: AZUL_LOGIN,
    },

    "&.Mui-focused fieldset": {
      borderColor: AZUL_LOGIN,
    },
  },
};

export function SearLogo() {
  return (
    <Box
      sx={{
        textAlign: "center",
        color: "#ffffff",
        mb: "18px",
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: { xs: "56px", sm: "64px" },
          fontWeight: 800,
          letterSpacing: "1px",
          lineHeight: 1,
          textShadow: "0 2px 10px rgba(0, 42, 135, 0.15)",
        }}
      >
        SEAR
      </Typography>

      <Box
        sx={{
          width: "260px",
          maxWidth: "100%",
          height: "1px",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          mx: "auto",
          my: "6px",
        }}
      />

      <Typography
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          lineHeight: 1.3,
        }}
      >
        Sistema Eletrônico de Atendimento Remoto
      </Typography>
    </Box>
  );
}

export default function TelaLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const { setCondicao, setRole, setLoginSuccess, setMantenedoraRegistrada } =
    useContext(CondicaoContext);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        api.login(),
        { email, senha: password },
        { withCredentials: true },
      );

      if (response.status === 200) {
        showSnackbar("Login bem-sucedido!", "success");

        const { role, usuario } = response.data;

        let mantenedoraJaRegistrada = null;

        if (role === "user") {
          const mantenedoraResponse = await axios.get(api.verify(), {
            withCredentials: true,
          });

          mantenedoraJaRegistrada =
            mantenedoraResponse.data.isRegistered === true;
        }

        setMantenedoraRegistrada(mantenedoraJaRegistrada);
        setRole(role);
        setCondicao(false);
        setLoginSuccess(true);

        if (role === "user") {
          navigate("/home/painel");
        } else if (role === "admin") {
          navigate("/admin/painel");
        } else if (role === "analyst") {
          navigate("/analista/painel");
        } else if (role === "superintendent") {
          if (usuario?.firstAccess === true) {
            navigate("/trocar-credenciais");
          } else {
            navigate("/admin");
          }
        }
      }
    } catch (error) {
      console.error("Erro no login:", error);

      const errorMessage = error.response?.data?.msg || "Erro ao fazer login";

      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <IdentidadeVisual variante="acesso">
      <Box
        sx={{
          width: "100%",
          maxWidth: "380px",
          mx: "auto",
          pb: { xs: 0, sm: 7 },
        }}
      >
        <SearLogo />

        <Box
          component="main"
          aria-labelledby="titulo-login"
          sx={{
            width: "100%",
            p: "22px",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.85)",
            background:
              "linear-gradient(145deg, #ffffff 0%, #f9fcff 60%, #edf5ff 100%)",
            boxShadow: "0 16px 36px rgba(0, 30, 104, 0.28)",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "#e0ecff",
            }}
          >
            <LockOutlined
              sx={{
                color: AZUL_LOGIN,
                fontSize: 32,
              }}
            />
          </Box>

          <Typography
            id="titulo-login"
            component="h1"
            sx={{
              mb: "24px",
              textAlign: "center",
              color: "#1b2149",
              fontSize: "22px",
              lineHeight: 1.2,
              fontWeight: 700,
            }}
          >
            Login
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              id="login-email"
              label="Email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      sx={{
                        color: AZUL_LOGIN,
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                ...campoLoginSx,
                mb: "20px",
              }}
            />

            <TextField
              id="login-senha"
              label="Senha"
              type={showPassword ? "text" : "password"}
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{
                        color: AZUL_LOGIN,
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                      aria-pressed={showPassword}
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{ color: AZUL_LOGIN }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                ...campoLoginSx,
                mb: "14px",
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                height: 44,
                borderRadius: "7px",
                backgroundColor: AZUL_LOGIN,
                color: "#ffffff",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,

                "&:hover": {
                  backgroundColor: "#0053d1",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Entrar"
              )}
            </Button>

          </Box>

          <Typography
            sx={{
              mt: "16px",
              textAlign: "center",
              fontSize: "13px",
              color: "#46516a",
            }}
          >
            Não tem uma conta?{" "}
            <Link
              to="/cadastro"
              style={{
                color: AZUL_LOGIN,
                textDecoration: "none",
              }}
            >
              Cadastre-se
            </Link>
          </Typography>

          {/* Recuperação de senha temporariamente oculta.
          <Typography
            sx={{
              mt: "12px",
              textAlign: "center",
              fontSize: "13px",
              lineHeight: 1.35,
              color: "#46516a",
            }}
          >
            Esqueceu sua senha?{" "}
            <Link
              to="/esqueci-senha"
              style={{
                color: AZUL_LOGIN,
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Clique aqui
            </Link>
          </Typography>
          */}

          <Divider sx={{ my: "20px", borderColor: "#d7e3f3" }} />

          <BotaoGuiaUtilizacao />
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </IdentidadeVisual>
  );
}
